import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import ReverseAuctionConsumer from "./ReverseAuctionConsumer";
import ReverseAuctionGenerator from "./ReverseAuctionGenerator";
import AuctionPageDashboard from "../AuctionPageDashboard";
import {
  activeView,
  auctionBidders,
  auctionGet,
} from "../../../../../Redux/Slices/auctionSlice";

// 1. CONFIGURATION: Extract static data outside component to prevent re-creation
const NOTIFICATION_TRIGGERS = [
  "BSRANotifyInvitedGenerator",
  "BSRANotifyConsumerTenderCreated",
  "BSRANotifyConsumerTenderTerminated",
  "BSRANotifyConsumerTenderUpdate",
  "BSRANotifyConsumerBidCreated",
  "BSRANotifyBidWinnerGenerator",
  "BSRANotifyBidRejectedGenerator",
  "BSRANotifyAmountChangeRequestGenerator",
  "BSRANotifyAmountChangeResponseConsumer",
  "BSRANotifyConsumerBidUpdated",
  "BSRANotifyBidWinnerGeneratorStageTwo",
  "BSRANotifyBidWinnerGeneratorStageOne",
  "BSRANotifyBidRejectedGeneratorStageOne",
  "BSRANotifyBidRejectedGeneratorStageTwo",
  "BSRANotifyGeneratorBidCreated",
  "BSRANotifyConsumerTenderActivation",
  "BSRANotifyGeneratorBidUpdated",
  "BSRANotifyConsumerTenderStart",
  "BSRANotifyConsumerTenderEnd",
  "BSRANotifyGeneratorTenderStart",
  "BSRANotifyGeneratorTenderEnd",
  "BSRANotifyConsumerBidStart",
  "BSRANotifyConsumerBidEnd",
  "BSRANotifyGeneratorBidStart",
  "BSRANotifyGeneratorBidEnd",
  "BSRANotifyConsumerStartAuction",
  "BSRANotifyConsumerEndAuction",
  "BSRANotifyGeneratorStartAuction",
  "BSRANotifyGeneratorEndAuction",
  "BSRANotifyTenderActivationAllGenerator",
  "BSRANotifyAmountChangeRequestConsumer",
  "BSRANotifyAmountChangeResponseGenerator",
  "BSRANotifyBidWinnerConsumer",
  "BSRAEmdBlockedNotification",
  "BSRAEmdReleasedNotification",
  "BSRAConsumerRaFeeBlockedNotification",
  "BSRAGenerateConsent",
  "BSRFQNotifyConsumerTenderCreated",
];

const VIEW_TO_API_MAP = {
  All: "All",
  Drafted: "Drafted",
  IPO: "IPO",
  "Live RA": "live_ra",
  Documentation: "TERMS",
  "Ongoing Contract": "CLOSED",
};

const RfqContainer = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  // 2. STATE SELECTORS: Safe access with fallbacks
  const rfq = location?.pathname === "/rfq";

  const role = useSelector((state) =>
    (state?.persistedReducer?.user?.activeRole || "").toLowerCase(),
  );

  const notify = useSelector((state) => state?.notificationSlice?.latest);
  const activeState = useSelector((state) => state?.auctionSlice?.activeView);

  // 3. LOGIC: Helper to get the correct API section parameter
  const getApiSection = useCallback((viewState) => {
    return VIEW_TO_API_MAP[viewState] || "All";
  }, []);

  // 4. ACTION: The core data fetching function
  const fetchAuctionData = useCallback(() => {
    const section = getApiSection(activeState);

    const payload = {
      section: section,
      tenderType: rfq ? "CB" : "RA",
    };

    if (role === "consumer") {
      dispatch(auctionGet(payload));
    } else if (role === "generator") {
      dispatch(auctionBidders(payload));
    }

    // NOTE: Removed `dispatch(activeView)` from here to prevent infinite loops.
    // The view state should drive the fetch, not vice versa.
  }, [activeState, role, rfq, dispatch, getApiSection]);

  // 5. EFFECT: Initial Load & State Changes
  useEffect(() => {
    // If activeState is somehow null on mount, default it to All
    if (!activeState) {
      dispatch(activeView("All"));
    } else {
      fetchAuctionData();
    }
  }, [activeState, dispatch, fetchAuctionData]);

  // 6. EFFECT: Notification Listener
  useEffect(() => {
    console.debug("Received notification:", notify);
    if (notify?.key && NOTIFICATION_TRIGGERS.includes(notify.key)) {
      if (role === "consumer") {
        fetchAuctionData();
      } else {
        setTimeout(() => {
          fetchAuctionData();
        }, 2000);
      }
    }
  }, [notify, fetchAuctionData]);

  // 7. RENDER
  return (
    <>
      {role === "consumer" ? (
        <ReverseAuctionConsumer rfq={rfq} />
      ) : role === "generator" ? (
        <ReverseAuctionGenerator rfq={rfq} />
      ) : (
        <AuctionPageDashboard />
      )}
    </>
  );
};

export default RfqContainer;
